<template>
  <div class="invoices-page">
    <b-row>
      <b-col>
        <b-card>
          <template #header>
            <strong>{{ $t('invoices.title') }}</strong>
            <div class="card-header-actions">
              <b-button 
                color="primary" 
                size="sm" 
                @click="loadInvoices"
                :disabled="loading"
              >
                <i class="fa fa-refresh" :class="{ 'fa-spin': loading }"></i> {{ $t('invoices.refresh') }}
              </b-button>
            </div>
          </template>
          <b-card-body>
            <!-- Outstanding Balance Banner -->
            <div v-if="$isAdmin() && pendingInvoices.length > 0" class="alert alert-warning mb-4">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <i class="fa fa-exclamation-triangle mr-2"></i>
                  <strong>{{ $t('invoices.unpaidInvoices') }}</strong>
                </div>
                <div class="text-right">
                  <div class="text-muted small">{{ $t('invoices.totalDebt') }}:</div>
                  <strong class="h5 mb-0">{{ formatAmount(pendingTotal, pendingCurrency) }}</strong>
                </div>
              </div>
            </div>

            <!-- Loading State -->
            <div v-if="loading" class="text-center py-5">
              <b-spinner variant="primary" />
              <p class="text-muted mt-2">{{ $t('invoices.loading') }}</p>
            </div>

            <!-- Error State -->
            <div v-else-if="error" class="alert alert-danger">
              <i class="fa fa-exclamation-circle mr-2"></i>
              {{ error }}
            </div>

            <!-- Empty State -->
            <div v-else-if="invoices.length === 0" class="text-center py-5">
              <i class="fa fa-file-text-o fa-3x text-muted mb-3"></i>
              <p class="text-muted">{{ $t('invoices.noInvoices') }}</p>
            </div>

            <!-- Invoices Table -->
            <div v-else>
              <b-table
                :items="invoices"
                :fields="fields"
                :per-page="perPage"
                :current-page="currentPage"
                hover
                responsive
                sort-icon-left
                class="invoices-table"
              >
                <template #cell(invoice_number)="{ item }">
                  <div class="invoice-number">
                    <strong>{{ item.invoice_number }}</strong>
                    <div class="invoice-date">{{ formatDate(item.CreatedAt) }}</div>
                  </div>
                </template>

                <template #cell(status)="{ item }">
                  <b-badge :variant="getStatusColor(item.status)" class="status-badge">
                    {{ getStatusLabel(item.status) }}
                  </b-badge>
                </template>

                <template #cell(total)="{ item }">
                  <div class="invoice-total">
                    <div class="total-amount">{{ formatAmount(item.total, item.currency) }}</div>
                  </div>
                </template>

                <template #cell(due_date)="{ item }">
                  <span :class="{ 'text-danger font-weight-bold': isOverdue(item) }">
                    {{ formatDate(item.due_date) }}
                  </span>
                </template>

                <template #cell(actions)="{ item }">
                  <b-button
                    variant="primary"
                    size="sm"
                    @click="viewInvoice(item)"
                    class="mr-1"
                  >
                    <i class="fa fa-eye"></i> {{ $t('invoices.view') }}
                  </b-button>
                  <b-button
                    variant="success"
                    size="sm"
                    @click="downloadInvoice(item)"
                  >
                    <i class="fa fa-download"></i>
                  </b-button>
                </template>
              </b-table>

              <b-pagination
                v-if="invoices.length > perPage"
                v-model="currentPage"
                :total-rows="invoices.length"
                :per-page="perPage"
                align="center"
                class="mt-3"
              </b-pagination>
            </div>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <!-- Invoice Details Modal -->
    <main-dialog
      v-model="showModal"
      centered
      size="xl"
      hide-footer
    >
      <template #modal-header>
        <h5 class="modal-title">{{ $t('invoices.invoice') }} {{ selectedInvoice ? selectedInvoice.invoice_number : '' }}</h5>
        <button type="button" class="close" @click="showModal = false">
          <span aria-hidden="true">&times;</span>
        </button>
      </template>

      <div v-if="selectedInvoice" class="invoice-details">
        <!-- Invoice Header -->
        <div class="invoice-header-section">
          <b-row>
            <b-col md="8">
              <h4 class="mb-3">{{ selectedInvoice.invoice_number }}</h4>
              <p v-if="selectedInvoice.description" class="text-muted mb-2">
                {{ selectedInvoice.description }}
              </p>
              <div class="invoice-meta">
                <span class="meta-item">
                  <i class="fa fa-calendar mr-1"></i>
                  {{ $t('invoices.issued') }}: {{ formatDate(selectedInvoice.CreatedAt) }}
                </span>
                <span class="meta-item">
                  <i class="fa fa-clock-o mr-1"></i>
                  {{ $t('invoices.due') }}: {{ formatDate(selectedInvoice.due_date) }}
                </span>
              </div>
            </b-col>
            <b-col md="4" class="text-right">
              <b-badge :variant="getStatusColor(selectedInvoice.status)" class="status-badge-large mb-2">
                {{ getStatusLabel(selectedInvoice.status) }}
              </b-badge>
              <div class="invoice-total-display">
                <div class="total-label">{{ $t('invoices.total') }}</div>
                <div class="total-value">{{ formatAmount(selectedInvoice.total, selectedInvoice.currency) }}</div>
              </div>
            </b-col>
          </b-row>
        </div>

        <!-- Line Items -->
        <div class="line-items-section mt-4">
          <h6 class="section-title">{{ $t('invoices.lineItems') }}</h6>
          <b-table
            v-if="selectedInvoice.line_items && selectedInvoice.line_items.length > 0"
            :items="selectedInvoice.line_items"
            :fields="lineItemFields"
            small
            class="line-items-table"
          >
            <template #cell(description)="{ item }">
              <div class="line-item-desc">
                {{ item.description }}
                <div v-if="item.option_code" class="option-code text-muted small">
                  {{ $t('invoices.optionCode') }}: {{ item.option_code }}
                </div>
              </div>
            </template>

            <template #cell(quantity)="{ item }">
              {{ item.quantity }}
            </template>

            <template #cell(unit_price)="{ item }">
              {{ formatAmount(item.unit_price, selectedInvoice.currency) }}
            </template>

            <template #cell(amount)="{ item }">
              <strong>{{ formatAmount(item.amount, selectedInvoice.currency) }}</strong>
            </template>
          </b-table>
          <div v-else class="text-muted text-center py-3">
            {{ $t('invoices.noLineItems') }}
          </div>
        </div>

        <!-- Totals Breakdown -->
        <div class="totals-section mt-4">
          <b-row>
            <b-col md="6" offset-md="6">
              <table class="totals-table">
                <tr>
                  <td>{{ $t('invoices.subtotal') }}:</td>
                  <td class="text-right">{{ formatAmount(selectedInvoice.subtotal, selectedInvoice.currency) }}</td>
                </tr>
                <tr v-if="selectedInvoice.discount > 0">
                  <td>{{ $t('invoices.discount') }}:</td>
                  <td class="text-right text-success">-{{ formatAmount(selectedInvoice.discount, selectedInvoice.currency) }}</td>
                </tr>
                <tr v-if="selectedInvoice.tax_amount > 0">
                  <td>{{ $t('invoices.tax') }} ({{ selectedInvoice.tax_rate }}%):</td>
                  <td class="text-right">{{ formatAmount(selectedInvoice.tax_amount, selectedInvoice.currency) }}</td>
                </tr>
                <tr class="total-row">
                  <td><strong>{{ $t('invoices.total') }}:</strong></td>
                  <td class="text-right"><strong>{{ formatAmount(selectedInvoice.total, selectedInvoice.currency) }}</strong></td>
                </tr>
                <tr v-if="selectedInvoice.amount_paid > 0">
                  <td>{{ $t('invoices.paid') }}:</td>
                  <td class="text-right text-success">{{ formatAmount(selectedInvoice.amount_paid, selectedInvoice.currency) }}</td>
                </tr>
                <tr v-if="selectedInvoice.amount_due > 0" class="due-row">
                  <td><strong>{{ $t('invoices.owes') }}:</strong></td>
                  <td class="text-right"><strong class="text-danger">{{ formatAmount(selectedInvoice.amount_due, selectedInvoice.currency) }}</strong></td>
                </tr>
              </table>
            </b-col>
          </b-row>
        </div>

        <!-- Payment Info -->
        <div v-if="selectedInvoice.paid_at" class="payment-info-section mt-4">
          <h6 class="section-title">{{ $t('invoices.paymentInformation') }}</h6>
          <b-row>
            <b-col md="6">
              <div class="info-item">
                <span class="info-label">{{ $t('invoices.paymentDate') }}:</span>
                <span class="info-value">{{ formatDate(selectedInvoice.paid_at) }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">{{ $t('invoices.paymentMethod') }}:</span>
                <span class="info-value">{{ formatPaymentMethod(selectedInvoice.payment_method) }}</span>
              </div>
            </b-col>
            <b-col md="6">
              <div v-if="selectedInvoice.transaction_id" class="info-item">
                <span class="info-label">{{ $t('invoices.transactionId') }}:</span>
                <span class="info-value"><code>{{ selectedInvoice.transaction_id }}</code></span>
              </div>
              <div v-if="selectedInvoice.payment_reference" class="info-item">
                <span class="info-label">{{ $t('invoices.reference') }}:</span>
                <span class="info-value"><code>{{ selectedInvoice.payment_reference }}</code></span>
              </div>
            </b-col>
          </b-row>
        </div>

        <!-- Notes -->
        <div v-if="selectedInvoice.notes" class="notes-section mt-4">
          <h6 class="section-title">{{ $t('invoices.notes') }}</h6>
          <p class="notes-text">{{ selectedInvoice.notes }}</p>
        </div>

        <!-- Actions -->
        <div class="modal-actions mt-4 pt-3 border-top">
          <b-button variant="secondary" @click="showModal = false">
            {{ $t('invoices.close') }}
          </b-button>
          <b-button variant="info" @click="downloadInvoice(selectedInvoice)" class="ml-2">
            <i class="fa fa-download mr-1"></i> {{ $t('invoices.download') }}
          </b-button>
          <b-button v-if="selectedInvoice.amount_due > 0 && cardPaymentsEnabled" variant="success" class="ml-2" @click="payInvoice(selectedInvoice)">
            <i class="fa fa-credit-card mr-1"></i> {{ $t('invoices.payNow') }}
          </b-button>
        </div>
      </div>
    </main-dialog>
  </div>
</template>

<script>
import Env from '@/plugins/protokol/lib/Env'
export default {
  name: 'Invoices',
  data() {
    return {
      invoices: [],
      loading: false,
      error: null,
      showModal: false,
      selectedInvoice: null,
      currentPage: 1,
      perPage: 10
    }
  },
  computed: {
    fields() {
      return [
        { key: 'invoice_number', label: this.$t('invoices.invoiceNumber'), sortable: true, thClass: 'text-left', tdClass: 'text-left' },
        { key: 'status', label: this.$t('invoices.status'), sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'total', label: this.$t('invoices.amount'), sortable: true, thClass: 'text-right', tdClass: 'text-right' },
        { key: 'due_date', label: this.$t('invoices.dueDate'), sortable: true, thClass: 'text-center', tdClass: 'text-center' },
        { key: 'actions', label: '', sortable: false, thClass: 'text-center', tdClass: 'text-center' }
      ]
    },
    lineItemFields() {
      return [
        { key: 'description', label: this.$t('invoices.description'), thClass: 'text-left', tdClass: 'text-left' },
        { key: 'quantity', label: this.$t('invoices.quantity'), thClass: 'text-center', tdClass: 'text-center' },
        { key: 'unit_price', label: this.$t('invoices.price'), thClass: 'text-right', tdClass: 'text-right' },
        { key: 'amount', label: this.$t('invoices.total'), thClass: 'text-right', tdClass: 'text-right' }
      ]
    },
    pendingInvoices() {
      return this.invoices.filter(inv => inv.status === 'pending' || inv.status === 'overdue')
    },
    pendingTotal() {
      return this.pendingInvoices.reduce((sum, inv) => sum + inv.amount_due, 0)
    },
    pendingCurrency() {
      // Get currency from first pending invoice, or default to EUR
      if (this.pendingInvoices.length > 0) {
        return this.pendingInvoices[0].currency
      }
      return 'EUR'
    },
    cardPaymentsEnabled() {
      return Env.get('CARD_PAYMENTS_ENABLED') === true || Env.get('CARD_PAYMENTS_ENABLED') === 'true'
    }
  },
  mounted() {
    this.loadInvoices()
  },
  methods: {
    async loadInvoices() {
      this.loading = true
      this.error = null

      try {
        const response = await this.$transactionApi.get('/billing/invoices')
        this.invoices = response.data || []
      } catch (err) {
        console.error('Error loading invoices:', err)
        this.error = err.response && err.response.data && err.response.data.message 
          ? err.response.data.message 
          : 'Greška pri učitavanju faktura'
      } finally {
        this.loading = false
      }
    },

    viewInvoice(invoice) {
      this.selectedInvoice = invoice
      this.showModal = true
    },

    async downloadInvoice(invoice) {
      try {
        const response = await this.$transactionApi.get(`/billing/invoice/${invoice.uuid}/pdf`, {
          responseType: 'blob'
        })
        
        // Create a blob URL and trigger download
        const blob = new Blob([response.data], { type: 'application/pdf' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `${invoice.invoice_number}.pdf`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        
        this.$bvToast.toast(this.$t('invoices.downloadSuccess'), {
          variant: 'success',
          solid: true,
          autoHideDelay: 2000
        })
      } catch (err) {
        console.error('Error downloading invoice:', err)
        const message = err.response && err.response.data && err.response.data.message 
          ? err.response.data.message 
          : this.$t('invoices.downloadError')
        this.$bvToast.toast(message, {
          variant: 'danger',
          solid: true
        })
      }
    },

    formatAmount(cents, currency = 'EUR') {
      if (cents === null || cents === undefined) return '€0.00'
      
      const currencySymbols = {
        'USD': '$',
        'EUR': '€',
        'RSD': 'RSD'
      }
      
      const symbol = currencySymbols[currency] || currency
      const amount = (cents / 100).toFixed(2)
      
      if (currency === 'USD') {
        return symbol + amount
      }
      
      return amount + ' ' + symbol
    },

    formatDate(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleDateString('sr-RS', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      })
    },

    getStatusLabel(status) {
      const key = `invoices.${status}`
      return this.$t(key)
    },

    getStatusColor(status) {
      const colors = {
        draft: 'secondary',
        pending: 'warning',
        paid: 'success',
        canceled: 'secondary',
        refunded: 'info',
        overdue: 'danger',
        void: 'dark'
      }
      return colors[status] || 'secondary'
    },

    isOverdue(invoice) {
      if (invoice.status === 'paid' || !invoice.due_date) return false
      return new Date(invoice.due_date) < new Date()
    },

    formatPaymentMethod(method) {
      const key = `invoices.${method}`
      return this.$t(key)
    },

    async payInvoice(invoice) {
      const popup = this.$loadingPopup()
      
      try {
        const cardPaymentsEnabled = Env.get('CARD_PAYMENTS_ENABLED') === true || Env.get('CARD_PAYMENTS_ENABLED') === 'true'
        const availablePaymentMethods = []

        // If invoice is pending and was created with bankaccount payment method,
        // only card can clear the pending amount — but only if card is enabled
        if (invoice.status === 'pending' && invoice.payment_method === 'bankaccount') {
          if (cardPaymentsEnabled) {
            availablePaymentMethods.push('card')
          }
        } else {
          await this.$store.dispatch('fetchProjectInfo')
          const projectInfo = this.$store.getters.getProjectInfo

          if (cardPaymentsEnabled) {
            availablePaymentMethods.push('card')
          }
          if (projectInfo && projectInfo.allow_bank_payment === true) {
            availablePaymentMethods.push('bankaccount')
          }
        }

        if (availablePaymentMethods.length === 0) {
          this.$bvToast.toast(this.$t('countryNotSupported'), {
            variant: 'danger',
            solid: true
          })
          return
        }

        const Checkout = (await import('@/lib/Checkout')).default
        const checkout = new Checkout()

        const checkoutData = {
          product_type: 'invoice',
          reference: invoice.uuid,
          lang: this.$store.getters.getLocale,
          currency: invoice.currency,
          available_payment_methods: availablePaymentMethods
        }

        const link = await checkout.getCheckoutLink(checkoutData)
        window.open(link.data, '_blank')
      } catch (err) {
        console.error('Error generating checkout link:', err)
        const message = err.response && err.response.data && err.response.data.message 
          ? err.response.data.message 
          : this.$t('invoices.paymentError')
        this.$bvToast.toast(message, {
          variant: 'danger',
          solid: true
        })
      } finally {
        popup.hide()
      }
    }
  }
}
</script>

<style scoped>
.invoices-page {
  padding: 20px;
}

.card-header-actions {
  display: flex;
  gap: 10px;
}

/* Table Styling */
.invoice-number strong {
  font-size: 0.95rem;
}

.invoice-date {
  font-size: 0.8rem;
  color: #adb5bd;
  margin-top: 2px;
}

.status-badge {
  padding: 6px 12px;
  font-size: 0.85rem;
  font-weight: 500;
}

.invoice-total {
  text-align: right;
}

.total-amount {
  font-weight: 600;
  font-size: 1rem;
}

.amount-due {
  font-size: 0.8rem;
  margin-top: 2px;
}

/* Modal Styling */
::v-deep .modal-content {
  background: var(--bg-main-end);
  color: #e9ecef;
  border: 1px solid #3a4a5c;
}

::v-deep .modal-header {
  background: var(--bg-card-secondary);
  border-bottom: 1px solid #3a4a5c;
  color: var(--text-primary);
}

::v-deep .modal-header .close {
  color: var(--text-primary);
  opacity: 0.8;
  text-shadow: none;
}

::v-deep .modal-header .close:hover {
  opacity: 1;
}

::v-deep .modal-title {
  color: var(--text-primary);
}

.invoice-details {
  padding: 10px;
}

.invoice-header-section {
  padding-bottom: 20px;
  border-bottom: 2px solid #3a4a5c;
}

.invoice-header-section h4 {
  color: var(--text-primary);
}

.invoice-meta {
  display: flex;
  gap: 20px;
  margin-top: 10px;
}

.meta-item {
  font-size: 0.9rem;
  color: #adb5bd;
}

.status-badge-large {
  font-size: 1rem;
  padding: 8px 16px;
  display: inline-block;
}

.invoice-total-display {
  margin-top: 10px;
}

.total-label {
  font-size: 0.85rem;
  color: #adb5bd;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.total-value {
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-top: 5px;
}

.section-title {
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 15px;
  padding-bottom: 8px;
  border-bottom: 1px solid #3a4a5c;
}

/* Line Items Table */
.line-items-table {
  font-size: 0.9rem;
  margin-bottom: 0;
}

::v-deep .line-items-table thead th {
  background: var(--bg-card-secondary);
  color: var(--text-primary);
  border: none;
  font-weight: 600;
  padding: 10px;
}

::v-deep .line-items-table tbody td {
  background: var(--bg-card);
  color: #e9ecef;
  border: none;
  border-bottom: 1px solid #3a4a5c;
  padding: 10px;
}

.line-item-desc {
  color: #e9ecef;
}

/* Totals Table */
.totals-table {
  width: 100%;
  font-size: 0.95rem;
  color: #e9ecef;
}

.totals-table tr {
  border-bottom: 1px solid #3a4a5c;
}

.totals-table td {
  padding: 8px 0;
}

.totals-table .total-row {
  border-top: 2px solid #3a4a5c;
  font-size: 1.1rem;
  color: var(--text-primary);
}

.totals-table .total-row td {
  padding-top: 12px;
}

.totals-table .due-row {
  border-top: 1px solid #3a4a5c;
  color: var(--text-primary);
}

/* Payment Info */
.payment-info-section {
  background: var(--bg-card-secondary);
  padding: 15px;
  border-radius: 6px;
  border: 1px solid #3a4a5c;
}

.info-item {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
}

.info-label {
  font-weight: 500;
  color: #adb5bd;
}

.info-value {
  color: var(--text-primary);
}

.info-value code {
  background: var(--bg-main-end);
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.85rem;
  color: #66d9ef;
  border: 1px solid #3a4a5c;
}

/* Notes */
.notes-section {
  background: rgba(255, 193, 7, 0.15);
  border: 1px solid rgba(255, 193, 7, 0.3);
  padding: 15px;
  border-radius: 6px;
}

.notes-text {
  margin: 0;
  color: #ffc107;
}

/* Modal Actions */
.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
</style>
